/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { ViewPresetsListFilter } from '../store/viewPresets/types';
import type {
  ViewPresetListItem,
  WorkspacePresetLabels,
  WorkspacePresetListItem,
} from '../store/workspace/types';
import type { WorkspaceListFilter } from '../store/workspaceList/types';
import { PresetFilterType } from '../store/workspaceList/types';

export const getCleanSearchQuery = (searchQuery: string): string =>
  searchQuery.trim().split(/\s+/).join();

export const getPresetTitle = (
  title: string,
  hasChanges: boolean,
  isNew: boolean,
  t: TFunction,
): string => {
  if (!hasChanges) {
    return title;
  }

  const suffix = isNew
    ? t('workspace-modified-existing')
    : t('workspace-modified-new');
  return `${title} (${suffix})`;
};

export const setDocumentTitle = (
  workspaceTitle: string,
  appTitle?: string,
): void => {
  const fallbackAppTitle = appTitle || 'GeoWeb';
  document.title = workspaceTitle
    ? `${workspaceTitle} - ${fallbackAppTitle}`
    : fallbackAppTitle;
};

export const humanizeLabelStrings = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

export const filterAndSortPresets = <
  T extends WorkspacePresetListItem | ViewPresetListItem,
>(
  presets: T[],
  selectedFilters: WorkspaceListFilter[] | ViewPresetsListFilter[],
  username: string,
  isAdmin: boolean,
  labelFilters?: WorkspacePresetLabels,
  searchQuery?: string,
): T[] => {
  const selectedIds = selectedFilters
    .filter((filter) => filter.isSelected)
    .map((filter) => filter.id);

  const expectedIdsForAll = selectedFilters.map((filter) => filter.id);

  const isAllSelected = expectedIdsForAll.every((id) =>
    selectedIds.includes(id),
  );

  const scopeFiltered = isAllSelected
    ? presets.filter((preset) => {
        if (isAdmin) {
          // Admin users: Only system + shared, never personal
          return (
            preset.scope === 'system' ||
            (preset.scope === 'user' && preset.is_shared)
          );
        }
        // Regular users: Keep everything
        return true;
      })
    : presets.filter((preset) => {
        return (
          (selectedIds.includes(PresetFilterType.system) &&
            preset.scope === 'system') ||
          (selectedIds.includes(PresetFilterType.personal) &&
            preset.scope === 'user' &&
            preset.username === username) ||
          (selectedIds.includes(PresetFilterType.shared) &&
            preset.scope === 'user' &&
            preset.is_shared &&
            (isAdmin || preset.username !== username))
        );
      });

  const labelFiltered = ((): T[] => {
    if (!labelFilters) {
      return scopeFiltered;
    }

    const activeGroups = (
      Object.keys(labelFilters) as (keyof WorkspacePresetLabels)[]
    )
      .map((key) => ({
        key,
        values: labelFilters[key] ?? [],
      }))
      .filter(({ values }) => values.length > 0);

    if (activeGroups.length === 0) {
      return scopeFiltered;
    }

    const matchesLabelFilters = (preset: T): boolean => {
      const presetLabels = preset.labels ?? {};

      return activeGroups.every(({ key, values: selectedValues }) => {
        const presetValues = (presetLabels[key] ?? []) as string[];

        return selectedValues.some((label) => presetValues.includes(label));
      });
    };

    return scopeFiltered.filter(matchesLabelFilters);
  })();

  const systemPresets = labelFiltered
    .filter((preset) => preset.scope === 'system')
    .sort((presetA, presetB) => presetA.title.localeCompare(presetB.title));

  const personalPresets = labelFiltered
    .filter((preset) => preset.scope === 'user' && preset.username === username)
    .sort((presetA, presetB) => presetA.title.localeCompare(presetB.title));

  const sharedPresets = labelFiltered
    .filter((preset) => preset.scope === 'user' && preset.username !== username)
    .sort((presetA, presetB) => presetA.title.localeCompare(presetB.title));

  const filteredByQuery = [
    ...systemPresets,
    ...personalPresets,
    ...sharedPresets,
  ].filter((preset) => {
    if (!searchQuery) {
      return true;
    }
    const terms = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const fields = [preset.title, preset.abstract, preset.description]
      .filter(Boolean)
      .map((fil) => fil!.toLowerCase());
    return terms.every((term) => fields.some((field) => field.includes(term)));
  });

  return filteredByQuery;
};
